import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import moment from "moment";
import { Range } from "react-range";
import { getProgressBarColour } from "../../../../../utils/generalUtils";
import {
  ContributorStack,
  LinkChip,
  QokrAvatar,
} from "../QokrPrimitives/QokrPrimitives";
import QokrConfirmDelete from "../QokrConfirmDelete/QokrConfirmDelete";
import "../QokrPeoplePicker/_qokr-people-picker.scss";
import "./_qokr-scorecard-card.scss";

// ═══════════════════════════════════════════════════════════════════════════
// One objective on the Quarterly OKR Scorecard (§11) — the prototype's card
// (design/qokr/prototype/screens/QuarterlyScorecard.js), on the Annual OKR
// scorecard's `okrs-*` styles.
//
// ⚠️ SHARED WITH THE COACH APP, BYTE-IDENTICAL, at the same folder depth
// (`_shared/QokrScorecardCard/`). The coach renders it read only. That is why
// it reads nothing from a store and does NOT import `_okr-scorecard.scss`:
// that sheet lives at a different path in each app, so each PAGE imports it.
//
// Collapsed: number, statement, owner · contributors · link (+ the page's
// optional `metaAction`, e.g. the CEO app's "Open OKR plan"), ONE progress
// bar, status, chevron. Open: Key Results then Initiatives, each with its %.
//
// ⚠️ EVERYTHING IS EDITED IN PLACE — NO POPUPS (2026-10-21, by instruction).
// Where the objective's `can_edit_*` flags allow it (and `writable`):
//   • statement, key result and initiative text — click to edit; Enter or
//     leaving the field saves, Esc cancels; "+ Add" rows; a bin per row.
//   • owner (company objectives) — click the name for a people menu.
//   • contributors — click the faces (or "+ Add contributors").
//   • progress — drag a key result's or an initiative's bar.
//   • status — the RAG menu.
// KR + initiative text is saved as ONE full replace (§11 `…/content`): kept
// ids keep their % (handoff 32 §1.3); a row without an id starts at 0. The
// objective's own % shown here is the average of its key results (the
// prototype's rule), else of its initiatives, else its own `progress` — the BE
// does NOT roll KR % into the objective (handoff 32 §1.4), so this is display only.
// ═══════════════════════════════════════════════════════════════════════════

const STATUS_OPTIONS = [
  { value: "AT_RISK", label: "At Risk", tone: "risk" },
  { value: "OFF_TRACK", label: "Off Track", tone: "off" },
  { value: "ON_TRACK", label: "On Track", tone: "on" },
];
const NOT_STARTED = { value: null, label: "Not Started", tone: "idle" };
const statusOption = (value) =>
  STATUS_OPTIONS.find((option) => option.value === value) || NOT_STARTED;

export const clampPercent = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? Math.min(100, Math.max(0, Math.round(n))) : 0;
};

const average = (rows) =>
  Math.round(
    rows.reduce((sum, row) => sum + clampPercent(row.progress), 0) /
      rows.length,
  );

// See the header.
export const objectivePercent = (objective) => {
  const krs = objective?.key_results || [];
  if (krs.length && krs.some((kr) => kr.progress != null)) return average(krs);
  const initiatives = objective?.initiatives || [];
  if (initiatives.length) return average(initiatives);
  return clampPercent(objective?.progress);
};

// `getProgressBarColour`'s bands as readable text (the Annual scorecard's).
export const percentInk = (value) =>
  value <= 30 ? "#c22f2f" : value <= 70 ? "#9c5115" : "#10794a";

const ChevronIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

// ── Progress ────────────────────────────────────────────────────────────────
const ProgressBar = ({ percent, level }) => (
  <span className={`okrs-prog okrs-prog--${level}`}>
    <span className="okrs-bar">
      <span
        className="okrs-bar-fill"
        style={{
          width: `${percent}%`,
          background: getProgressBarColour(percent),
        }}
      />
    </span>
    <span className="okrs-pct" style={{ color: percentInk(percent) }}>
      {percent}%
    </span>
  </span>
);

// Thumb always visible; one write per drag (the Annual scorecard's slider).
// ⚠️ NEVER DISABLED WHILE SAVING: the store writes optimistically and the
// PATCH runs in the background, so the bar stays draggable and shows the new
// value at once — a failure snaps it back.
const ProgressSlider = ({ value, label, onCommit }) => {
  const [percent, setPercent] = useState(clampPercent(value));
  useEffect(() => setPercent(clampPercent(value)), [value]);

  return (
    <span className="okrs-prog okrs-prog--kr is-editable" data-no-toggle>
      <Range
        step={1}
        min={0}
        max={100}
        values={[percent]}
        onChange={(values) => setPercent(clampPercent(values[0]))}
        onFinalChange={(values) => {
          const next = clampPercent(values[0]);
          if (next !== clampPercent(value)) onCommit(next);
        }}
        renderTrack={({ props, children }) => (
          <span
            {...props}
            className="okrs-bar okrs-bar--track"
            style={props.style}
          >
            <span
              className="okrs-bar-fill"
              style={{
                width: `${percent}%`,
                background: getProgressBarColour(percent),
              }}
            />
            {children}
          </span>
        )}
        renderThumb={({ props }) => (
          <span
            {...props}
            className="okrs-thumb"
            aria-label={label}
            style={props.style}
          />
        )}
      />
      <span className="okrs-pct" style={{ color: percentInk(percent) }}>
        {percent}%
      </span>
    </span>
  );
};

const RowProgress = ({ row, editable, label, onCommit }) =>
  editable && row.id ? (
    <ProgressSlider value={row.progress} label={label} onCommit={onCommit} />
  ) : (
    <ProgressBar percent={clampPercent(row.progress)} level="kr" />
  );

// ── Status: button + list so the coloured dots show in the menu ────────────
// Optimistic like progress: no saving state, the pill changes on pick.
const StatusControl = ({ value, editable, onChange }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const current = statusOption(value);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!editable) {
    return (
      <span className={`okrs-status okrs-status--${current.tone}`}>
        <span className="okrs-dot" aria-hidden="true" />
        <span className="okrs-status-label">{current.label}</span>
      </span>
    );
  }

  return (
    <div className="okrs-status-wrap" ref={rootRef} data-no-toggle>
      <button
        type="button"
        ref={triggerRef}
        className={`okrs-status okrs-status--${current.tone} is-editable${open ? " is-open" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
      >
        <span className="okrs-dot" aria-hidden="true" />
        <span className="okrs-status-label">{current.label}</span>
        <span className={`okrs-status-caret${open ? " is-flipped" : ""}`}>
          <ChevronIcon size={11} />
        </span>
      </button>
      {open ? (
        <div className="okrs-status-menu" role="listbox">
          {STATUS_OPTIONS.map((option) => (
            <button
              type="button"
              key={option.value}
              role="option"
              aria-selected={option.value === value}
              className={`okrs-status-option okrs-status-option--${option.tone}${
                option.value === value ? " is-selected" : ""
              }`}
              onClick={() => {
                setOpen(false);
                if (option.value !== value) onChange(option.value);
              }}
            >
              <span className="okrs-dot" aria-hidden="true" />
              <span className="okrs-status-option-label">{option.label}</span>
              {option.value === value ? (
                <i className="fa-solid fa-check" aria-hidden="true" />
              ) : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

// ── People menu (owner: pick one; contributors: tick several) ──────────────
// Portalled to <body> and positioned against its trigger, like the
// contributors menu in `QokrPeoplePicker` (whose `qokr-contrib-menu` styles
// it reuses), so the card's own layout can never clip it.
const MENU_WIDTH = 300;

const PeopleMenu = ({
  anchorRef,
  title,
  options,
  initial,
  multi,
  onPick,
  onClose,
}) => {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);
  const [selected, setSelected] = useState(initial);
  const selectedRef = useRef(initial);
  selectedRef.current = selected;

  useLayoutEffect(() => {
    const place = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const height = ref.current ? ref.current.offsetHeight : 0;
      const left = Math.max(
        12,
        Math.min(rect.left, window.innerWidth - MENU_WIDTH - 12),
      );
      const below = rect.bottom + 6;
      const top =
        below + height > window.innerHeight - 12
          ? Math.max(12, rect.top - 6 - height)
          : below;
      setPos({ top, left });
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [anchorRef]);

  useEffect(() => {
    const finish = () => onClose(selectedRef.current);
    const onDown = (event) => {
      if (ref.current?.contains(event.target)) return;
      if (anchorRef.current?.contains(event.target)) return;
      finish();
    };
    const onKey = (event) => {
      if (event.key === "Escape") finish();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [anchorRef, onClose]);

  return ReactDOM.createPortal(
    <div
      className="qokr-contrib-menu"
      ref={ref}
      role={multi ? "menu" : "listbox"}
      style={{
        position: "fixed",
        width: MENU_WIDTH,
        top: pos ? pos.top : -9999,
        left: pos ? pos.left : -9999,
        visibility: pos ? "visible" : "hidden",
      }}
    >
      <div className="qokr-contrib-menu__head">
        <span>{title}</span>
        {multi ? <span>{selected.length} selected</span> : null}
      </div>
      {!options.length ? (
        <div className="qokr-contrib-menu__empty">
          <span className="qokr-contrib-menu__empty-icon">
            <i className="fa-solid fa-user-group" aria-hidden="true" />
          </span>
          <p className="qokr-contrib-menu__empty-title">No one to add yet</p>
          <p className="qokr-contrib-menu__empty-text">
            Add team members to choose from.
          </p>
        </div>
      ) : null}
      <div className="qokr-contrib-menu__list">
        {options.map((person) => {
          const id = String(person.user_id);
          const checked = selected.includes(id);
          return (
            <button
              key={id}
              type="button"
              role={multi ? "menuitemcheckbox" : "option"}
              aria-checked={multi ? checked : undefined}
              aria-selected={multi ? undefined : checked}
              className={`qokr-contrib-menu__item${checked ? " is-checked" : ""}`}
              onClick={() => {
                if (!multi) {
                  onPick(id);
                  return;
                }
                setSelected((ids) =>
                  ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id],
                );
              }}
            >
              <span className="qokr-contrib-menu__check">
                {checked ? (
                  <i className="fa-solid fa-check" aria-hidden="true" />
                ) : null}
              </span>
              <QokrAvatar person={person} size="xs" />
              <span className="qokr-contrib-menu__name">{person.name}</span>
              <span className="qokr-contrib-menu__role">{person.role}</span>
            </button>
          );
        })}
      </div>
    </div>,
    document.body,
  );
};

// ── Text edited in place ────────────────────────────────────────────────────
// ⚠️ NOTHING SAVES WITHOUT AN EXPLICIT YES (2026-10-21, by instruction — a
// stray click on a statement must never change it). Clicking opens the field
// with **Save** / **Cancel** under it; only Save (or Enter, the same thing
// from the keyboard) writes. Leaving the field does NOT save: unchanged, it
// just closes; changed, it stays open with the buttons so nothing typed is
// lost and nothing is saved by accident. Esc cancels. The two controls are
// icons only (✕ / ✓), inside the field. An empty save on an
// existing row is a cancel — removing is the bin's job (and asks too).
const InlineText = ({
  value,
  editable,
  className = "",
  placeholder,
  maxLength,
  startEditing = false,
  initialDraft,
  onSave,
  onCancel,
}) => {
  const [editing, setEditing] = useState(startEditing);
  const [draft, setDraft] = useState(initialDraft || value || "");
  const fieldRef = useRef(null);

  useEffect(() => {
    if (!editing) setDraft(value || "");
  }, [value, editing]);

  useEffect(() => {
    if (!editing || !fieldRef.current) return;
    const field = fieldRef.current;
    field.focus();
    field.setSelectionRange(field.value.length, field.value.length);
  }, [editing]);

  // Grow with the text, one line at rest.
  useLayoutEffect(() => {
    const field = fieldRef.current;
    if (!field) return;
    field.style.height = "auto";
    field.style.height = `${field.scrollHeight}px`;
  }, [draft, editing]);

  const cancel = () => {
    setDraft(value || "");
    setEditing(false);
    onCancel?.();
  };

  const changed = draft.trim() !== (value || "").trim();

  // ⚠️ DOESN'T WAIT FOR THE API (2026-10-21, by instruction): ✓ closes the
  // field at once and the new text shows straight away (the store writes it
  // optimistically). If the save fails, the store puts the old text back and
  // the field reopens with what was typed, so nothing is lost.
  const save = () => {
    const next = draft.trim();
    if (!next || next === (value || "").trim()) {
      cancel();
      return;
    }
    setEditing(false);
    Promise.resolve(onSave(next)).catch(() => {
      setDraft(next);
      setEditing(true);
    });
  };

  if (!editable) return <span className={className}>{value}</span>;

  if (!editing) {
    return (
      <button
        type="button"
        className={`qokr-inline ${className}`}
        onClick={() => setEditing(true)}
        title="Click to edit"
        data-no-toggle
      >
        {value || (
          <span className="qokr-inline__placeholder">{placeholder}</span>
        )}
        <i className="fa-solid fa-pen qokr-inline__pen" aria-hidden="true" />
      </button>
    );
  }

  return (
    // ✕ / ✓ icons inside the field, bottom-right — the interview card's
    // inline editor, restyled here as `qokr-sc-edit` (sharing its class name
    // made the two stylesheets fight and squeezed these buttons).
    <span className="qokr-sc-edit" data-no-toggle>
      <textarea
        ref={fieldRef}
        className={`qokr-sc-edit__input ${className}`}
        rows={1}
        value={draft}
        maxLength={maxLength}
        placeholder={placeholder}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={(event) => {
          // Moving to ✕ / ✓ is not leaving.
          if (
            event.currentTarget.parentElement?.contains(event.relatedTarget)
          ) {
            return;
          }
          if (!changed) cancel();
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            save();
          } else if (event.key === "Escape") {
            event.preventDefault();
            cancel();
          }
        }}
      />
      <span className="qokr-sc-edit__actions">
        <button
          type="button"
          className="qokr-sc-edit__btn"
          onClick={cancel}
          aria-label="Cancel"
          title="Cancel (Esc)"
        >
          <i className="fa-solid fa-xmark" aria-hidden="true" />
        </button>
        <button
          type="button"
          className="qokr-sc-edit__btn qokr-sc-edit__btn--save"
          disabled={!draft.trim() || !changed}
          onClick={save}
          aria-label="Save"
          title="Save (Enter)"
        >
          <i className="fa-solid fa-check" aria-hidden="true" />
        </button>
      </span>
    </span>
  );
};

// ── Key results / initiatives: one section ─────────────────────────────────
const toContentRows = (rows, idKey) =>
  rows.map((row) => ({
    ...(row.id ? { [idKey]: row.id } : {}),
    statement: row.statement,
  }));

const RowSection = ({
  title,
  noun,
  rows,
  minRows,
  maxRows,
  maxChars,
  canEditText,
  canEditProgress,
  onSaveRows,
  onProgress,
}) => {
  // `false`, or the text the new row opens with ("" — or what was typed, if
  // its save failed and it reopens).
  const [adding, setAdding] = useState(false);
  // The row a bin was clicked on — removed only after the confirm.
  const [removing, setRemoving] = useState(null); // { index, statement }

  return (
    <>
      <div className="okrs-section-title qokr-sc-section">
        {title}
        <span className="okrs-section-count">{rows.length}</span>
      </div>
      {rows.map((row, index) => (
        <div className="okrs-line qokr-sc-line" key={row.id ?? `new-${index}`}>
          <span className="okrs-line-text">
            <span className="qokr-sc-num">{index + 1}</span>
            <span className="qokr-sc-line-body">
              <InlineText
                value={row.statement}
                editable={canEditText}
                maxLength={maxChars}
                onSave={(text) =>
                  onSaveRows(
                    rows.map((r, i) =>
                      i === index ? { ...r, statement: text } : r,
                    ),
                  )
                }
              />
              {row.updatedBy ? (
                <span className="qokr-sc-updated">
                  Updated by {row.updatedBy.name}
                  {row.updatedAt ? ` · ${moment(row.updatedAt).fromNow()}` : ""}
                </span>
              ) : null}
            </span>
          </span>
          <RowProgress
            row={row}
            editable={canEditProgress}
            label={`Progress for ${noun} ${index + 1}`}
            onCommit={(value) => onProgress(row, value)}
          />
          <span className="qokr-sc-line-tools">
            {canEditText ? (
              <button
                type="button"
                className="qokr-sc-remove"
                disabled={rows.length <= minRows}
                title={
                  rows.length <= minRows
                    ? `An objective needs at least ${minRows} ${noun}`
                    : `Remove this ${noun}`
                }
                aria-label={`Remove ${noun} ${index + 1}`}
                onClick={() => setRemoving({ index, statement: row.statement })}
              >
                <i className="fa-regular fa-trash-can" aria-hidden="true" />
              </button>
            ) : null}
          </span>
          <span />
        </div>
      ))}
      {!rows.length && adding === false ? (
        <p className="okrs-line-empty">No {noun}s on this objective.</p>
      ) : null}
      {canEditText && adding !== false ? (
        <div className="okrs-line qokr-sc-line">
          <span className="okrs-line-text">
            <span className="qokr-sc-num">{rows.length + 1}</span>
            <span className="qokr-sc-line-body">
              <InlineText
                key={`add-${adding}`}
                value=""
                initialDraft={adding}
                editable
                startEditing
                maxLength={maxChars}
                placeholder={`New ${noun}…`}
                onCancel={() => setAdding(false)}
                onSave={(text) => {
                  // Shown at once (optimistic); reopens with the text if the
                  // save fails.
                  setAdding(false);
                  return Promise.resolve(
                    onSaveRows([...rows, { id: null, statement: text }]),
                  ).catch(() => setAdding(text));
                }}
              />
            </span>
          </span>
        </div>
      ) : null}
      {removing ? (
        <QokrConfirmDelete
          title={`Remove this ${noun}?`}
          confirmLabel={`Remove ${noun}`}
          onClose={() => setRemoving(null)}
          onConfirm={() => {
            // Gone at once (optimistic); the store puts it back if the save
            // fails, and the page says why.
            const index = removing.index;
            setRemoving(null);
            Promise.resolve(
              onSaveRows(rows.filter((_, i) => i !== index)),
            ).catch(() => null);
          }}
        >
          <p className="mb-0">
            &ldquo;{removing.statement}&rdquo; and its progress are removed from
            this objective.
          </p>
        </QokrConfirmDelete>
      ) : null}
      {canEditText && adding === false && rows.length < maxRows ? (
        <button
          type="button"
          className="qokr-sc-add"
          onClick={() => setAdding("")}
        >
          <i className="fa-solid fa-plus" aria-hidden="true" />
          Add {noun}
        </button>
      ) : canEditText && adding === false ? (
        // At the cap: say so instead of the Add link vanishing.
        <span className="qokr-sc-limit">
          Up to {maxRows} {noun}s
        </span>
      ) : null}
    </>
  );
};

/**
 * @param objective   §11 objective, with `type` set ("company" | "individual").
 * @param label       The number column ("O1", "01").
 * @param linkLabel   Link chip text; `undefined` shows no chip.
 * @param writable    The quarter is still open (§11 `writable`). The coach
 *                    passes false, so nothing is editable.
 * @param members     `[{ user_id, name, role }]` for the people menus.
 * @param viewerId    The signed-in user — an objective's OWNER may edit it.
 * @param isCeo       The CEO may edit every objective.
 * @param limits      `{ KEY_RESULTS_MIN, KEY_RESULTS_MAX, INITIATIVES_MIN, INITIATIVES_MAX,
 *                    KEY_RESULT_MAX_CHARS, INITIATIVE_MAX_CHARS,
 *                    OBJECTIVE_MAX_CHARS }`.
 * @param on*         Handlers; each save returns a promise that REJECTS on
 *                    failure (the page shows the error), so a field can stay
 *                    open with what was typed.
 */
const QokrScorecardCard = ({
  objective,
  label,
  linkLabel,
  writable = false,
  defaultOpen = false,
  members = [],
  viewerId = null,
  isCeo = false,
  limits = {},
  onStatus,
  onKeyResultProgress,
  onInitiativeProgress,
  onSaveText,
  onSaveContent,
  onSaveAssignment,
  onDelete,
  // Optional node at the end of the meta line (the coach app passes none).
  metaAction = null,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const [menu, setMenu] = useState(null); // "owner" | "contributors"
  const ownerRef = useRef(null);
  const contributorsRef = useRef(null);

  const isCompany = objective.type === "company";
  const owner = objective.owner || objective.member;
  // ⚠️ THE OWNER AND THE CEO DO EVERYTHING; NOBODY ELSE DOES ANYTHING
  // (2026-10-21, by instruction) — on company AND individual objectives:
  // wording, key results and initiatives (edit / add / remove), progress,
  // status, contributors, owner (company objectives — an individual one's
  // person is fixed) and delete. Everyone else sees the card read only. This
  // replaces the per-objective `can_edit_*` flags, which §11 drew differently
  // (no content edits for a company owner, progress for the CEO on all);
  // where the BE still refuses, the save comes back 403 and the page says so.
  const isOwner =
    viewerId != null &&
    owner?.user_id != null &&
    String(owner.user_id) === String(viewerId);
  const canManage = writable && (isOwner || Boolean(isCeo));
  // Progress also follows the BE's own `can_edit_progress` (handoff 32 §1.1 —
  // it also grants the champion), so nobody the BE allows is left without
  // the slider. The coach's payload sends it `false`, and `writable` is off there.
  const canProgress =
    canManage || (writable && objective.can_edit_progress === true);
  const canStatus = canManage;
  const canContent = canManage;
  const canAssign = canManage;
  const canOwner = canManage && isCompany;

  const contributors = objective.contributors || [];
  const contributorIds = contributors.map((p) => String(p.user_id));
  const fixedId = owner?.user_id != null ? String(owner.user_id) : null;
  const percent = objectivePercent(objective);
  const bodyId = `qokr-sc-body-${objective.type}-${objective.objective_id}`;

  const keyResults = (objective.key_results || []).map((kr) => ({
    id: kr.key_result_id ?? null,
    statement: kr.statement || "",
    progress: kr.progress,
    updatedBy: kr.progress_updated_by,
    updatedAt: kr.progress_updated_at,
  }));
  const initiatives = (objective.initiatives || []).map((item) => ({
    id: item.initiative_id ?? null,
    statement: item.statement || "",
    progress: item.progress,
    updatedBy: item.progress_updated_by,
    updatedAt: item.progress_updated_at,
  }));

  // One full replace for both lists (§11): the side not being edited is sent
  // back as it is, ids and all.
  const saveRows = (which) => (rows) =>
    onSaveContent(objective, {
      key_results: toContentRows(
        which === "key_results" ? rows : keyResults,
        "key_result_id",
      ),
      initiatives: toContentRows(
        which === "initiatives" ? rows : initiatives,
        "initiative_id",
      ),
    });

  const onHeadClick = (event) => {
    if (
      event.target.closest(
        "[data-no-toggle], .qokr-link-chip, .okrs-status-wrap, textarea",
      )
    ) {
      return;
    }
    setOpen((was) => !was);
  };

  const others = members.filter((m) => String(m.user_id) !== fixedId);

  return (
    <article className={`okrs-card qokr-sc-card${open ? " is-open" : ""}`}>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className="okrs-card-head" onClick={onHeadClick}>
        <span className="okrs-num">{label}</span>

        <div className="okrs-card-main">
          <div className="okrs-statement">
            <InlineText
              value={objective.objective || "Untitled objective"}
              editable={canContent}
              maxLength={limits.OBJECTIVE_MAX_CHARS}
              onSave={(text) => onSaveText(objective, text)}
            />
          </div>
          <div className="okrs-meta">
            {owner ? (
              canOwner ? (
                <button
                  type="button"
                  ref={ownerRef}
                  className="okrs-person qokr-sc-person-btn"
                  title="Change owner"
                  data-no-toggle
                  onClick={() => setMenu("owner")}
                >
                  <QokrAvatar person={owner} size="sm" />
                  {owner.name}
                  <ChevronIcon size={11} />
                </button>
              ) : (
                <span className="okrs-person">
                  <QokrAvatar person={owner} size="sm" />
                  {owner.name}
                </span>
              )
            ) : null}

            {canAssign ? (
              <button
                type="button"
                ref={contributorsRef}
                className="qokr-sc-person-btn qokr-sc-contrib-btn"
                title="Change contributors"
                data-no-toggle
                onClick={() => setMenu("contributors")}
              >
                {contributors.length ? (
                  <ContributorStack people={contributors} max={3} />
                ) : (
                  <span className="qokr-sc-add-people">
                    <i className="fa-solid fa-plus" aria-hidden="true" />
                    Add contributors
                  </span>
                )}
              </button>
            ) : contributors.length ? (
              // ContributorStack writes "N contributors" itself.
              <ContributorStack people={contributors} max={3} />
            ) : null}

            {linkLabel !== undefined ? (
              <LinkChip
                label={linkLabel}
                kind={objective.type}
                relationship={objective.relationship}
              />
            ) : null}

            {metaAction ? (
              <span className="qokr-sc-meta-action" data-no-toggle>
                {metaAction}
              </span>
            ) : null}
          </div>
        </div>

        <ProgressBar percent={percent} level="obj" />

        <StatusControl
          value={objective.status}
          editable={canStatus}
          onChange={(value) => onStatus(objective, value)}
        />

        {/* Delete sits on the objective line, after the status (2026-10-21,
            by instruction); the page confirms before anything is removed. */}
        <span className="qokr-sc-head-tools">
          {canManage && onDelete ? (
            <button
              type="button"
              className="qokr-sc-delete"
              title="Delete objective"
              aria-label={`Delete ${label}`}
              data-no-toggle
              onClick={() => onDelete(objective)}
            >
              <i className="fa-regular fa-trash-can" aria-hidden="true" />
            </button>
          ) : null}
          <button
            type="button"
            className="okrs-chevron"
            aria-expanded={open}
            aria-controls={bodyId}
            aria-label={
              open
                ? "Hide key results and initiatives"
                : "Show key results and initiatives"
            }
            onClick={(event) => {
              event.stopPropagation();
              setOpen((was) => !was);
            }}
          >
            <ChevronIcon />
          </button>
        </span>
      </div>

      {open ? (
        <div className="okrs-card-body" id={bodyId}>
          <RowSection
            title="Key Results"
            noun="key result"
            rows={keyResults}
            minRows={limits.KEY_RESULTS_MIN || 1}
            maxRows={limits.KEY_RESULTS_MAX || 5}
            maxChars={limits.KEY_RESULT_MAX_CHARS}
            canEditText={canContent}
            canEditProgress={canProgress}
            onSaveRows={saveRows("key_results")}
            onProgress={(row, value) => onKeyResultProgress(row.id, value)}
          />
          <RowSection
            title="Initiatives"
            noun="initiative"
            rows={initiatives}
            minRows={limits.INITIATIVES_MIN || 1}
            maxRows={limits.INITIATIVES_MAX || 15}
            maxChars={limits.INITIATIVE_MAX_CHARS}
            canEditText={canContent}
            canEditProgress={canProgress}
            onSaveRows={saveRows("initiatives")}
            onProgress={(row, value) => onInitiativeProgress(row.id, value)}
          />
        </div>
      ) : null}

      {menu === "owner" ? (
        <PeopleMenu
          anchorRef={ownerRef}
          title="Owner"
          options={members}
          initial={fixedId ? [fixedId] : []}
          onPick={(id) => {
            setMenu(null);
            if (id === fixedId) return;
            onSaveAssignment(objective, {
              owner_user_id: id,
              // The new owner can't also be a contributor.
              contributor_ids: contributorIds.filter((x) => x !== id),
            }).catch(() => null);
          }}
          onClose={() => setMenu(null)}
        />
      ) : null}
      {menu === "contributors" ? (
        <PeopleMenu
          anchorRef={contributorsRef}
          title="Contributors"
          multi
          options={others}
          initial={contributorIds.filter((id) => id !== fixedId)}
          onClose={(ids) => {
            setMenu(null);
            const same =
              ids.length === contributorIds.length &&
              ids.every((id) => contributorIds.includes(id));
            if (!same) {
              onSaveAssignment(objective, { contributor_ids: ids }).catch(
                () => null,
              );
            }
          }}
        />
      ) : null}
    </article>
  );
};

export default QokrScorecardCard;
